// src/dataProvider/resources/events.ts
import { httpClient } from "../../httpClient";
import { pickClubId, withGlobalClubFilter } from "../clubScope";

const apiUrl = "/api";

function isOccAttendanceResource(resource: string) {
  return /^events\/occurrences\/\d+\/attendance$/.test(resource);
}
function isOccMediaResource(resource: string) {
  return /^events\/occurrences\/\d+\/media$/.test(resource);
}

function extractOccId(resource: string): number {
  const m = resource.match(/^events\/occurrences\/(\d+)\//);
  if (!m) throw new Error(`Kann occurrence id nicht aus resource lesen: ${resource}`);
  return Number(m[1]);
}

export function eventsResource(clubId: number | null) {
  return {
    // ---------- SERIES ----------
    series: {
      getList: async (params: any) => {
        const paramsWithClub = withGlobalClubFilter(params, clubId);
        const effectiveClubId = pickClubId(paramsWithClub, clubId);
        if (!effectiveClubId) throw new Error("clubId fehlt (bitte Verein auswählen)");

        const qs = new URLSearchParams();
        qs.set("club_id", String(effectiveClubId));
        // optional: include_inactive, etc.
        if (paramsWithClub?.filter?.include_inactive != null) {
          qs.set("include_inactive", String(paramsWithClub.filter.include_inactive));
        }

        const { json } = await httpClient(`${apiUrl}/events/series?${qs.toString()}`);
        const data = Array.isArray(json) ? json : json?.data ?? [];
        return { data, total: json?.total ?? data.length };
      },

      getOne: async (params: any) => {
        const { json } = await httpClient(`${apiUrl}/events/series/${params.id}`);
        return { data: json };
      },

      create: async (params: any) => {
        const effectiveClubId = pickClubId(params, clubId);
        if (!effectiveClubId) throw new Error("clubId fehlt (bitte Verein auswählen)");

        const payload = { ...params.data, club_id: effectiveClubId };
        const { json } = await httpClient(`${apiUrl}/events/series`, {
          method: "POST",
          body: JSON.stringify(payload),
        });
        return { data: json };
      },

      update: async (params: any) => {
        const { json } = await httpClient(`${apiUrl}/events/series/${params.id}`, {
          method: "PUT",
          body: JSON.stringify(params.data),
        });
        return { data: json };
      },

      delete: async (params: any) => {
        await httpClient(`${apiUrl}/events/series/${params.id}`, { method: "DELETE" });
        return { data: { id: params.id } };
      },
    },

    // ---------- OCCURRENCES ----------
    occurrences: {
     getList: async (params: any) => {
    const paramsWithClub = withGlobalClubFilter(params, clubId);
    const effectiveClubId = pickClubId(paramsWithClub, clubId);
    if (!effectiveClubId) throw new Error("clubId fehlt (bitte Verein auswählen)");

    const qs = new URLSearchParams();
    qs.set("club_id", String(effectiveClubId));

    // ✅ Filter wie bisher
    Object.entries(paramsWithClub.filter ?? {}).forEach(([k, v]) => {
      if (v != null && v !== "" && k !== "clubId" && k !== "club_id") {
        qs.set(k, String(v));
      }
    });

    // ✅ Pagination + Sort von React-Admin an Backend durchreichen
    const page = paramsWithClub?.pagination?.page ?? 1;
    const perPage = paramsWithClub?.pagination?.perPage ?? 25;
    qs.set("page", String(page));
    qs.set("perPage", String(perPage));

    const sortField = paramsWithClub?.sort?.field ?? "starts_at";
    const sortOrder = paramsWithClub?.sort?.order ?? "ASC";
    qs.set("sort", String(sortField));
    qs.set("order", String(sortOrder));

    const { json } = await httpClient(`${apiUrl}/events/occurrences?${qs.toString()}`);

    // ✅ Backend liefert jetzt { data, total }
    const data = Array.isArray(json) ? json : json?.data ?? [];
    const total = Number(json?.total ?? data.length);

    return { data, total };
  },

      // ✅ für Show/Detail
      getOne: async (params: any) => {
        const { json } = await httpClient(`${apiUrl}/events/occurrences/${params.id}`);
        return { data: json };
      },

      // ✅ Zusage/Absage
      setAttendance: async (resource: string, params: any) => {
        const occId = extractOccId(resource);
        const { json } = await httpClient(`${apiUrl}/events/occurrences/${occId}/attendance`, {
          method: "PUT",
          body: JSON.stringify(params.data), // params.data muss OBJECT sein!
        });
        return { data: json ?? { ok: true } };
      },

      // ✅ Media list/create (FormData)
      mediaGetList: async (resource: string) => {
        const occId = extractOccId(resource);
        const { json } = await httpClient(`${apiUrl}/events/occurrences/${occId}/media`);
        const data = Array.isArray(json) ? json : json?.data ?? [];
        return { data, total: data.length };
      },

      mediaCreate: async (resource: string, params: any) => {
        const occId = extractOccId(resource);

        // params.data ist FormData
        const { json } = await httpClient(`${apiUrl}/events/occurrences/${occId}/media`, {
          method: "POST",
          body: params.data, // FormData -> httpClient muss das durchreichen
        });
        return { data: json };
      },
    },

    // ---------- ROUTING HELPERS ----------
    matchers: {
      isOccAttendanceResource,
      isOccMediaResource,
      extractOccId,
    },
  };
}
